import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FolderKanban, Plus, Users } from "lucide-react";

const STATUS_TONE = {
  Open: "bg-blue-50 text-blue-800 border-blue-200",
  Watching: "bg-amber-50 text-amber-800 border-amber-200",
  Escalated: "bg-red-50 text-red-800 border-red-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-200",
};
const PRIORITY_TONE = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-teal-50 text-teal-700 border-teal-200",
};

export function StatusPill({ status }) {
  return (
    <span data-testid={`case-status-${status}`} className={`inline-flex items-center border px-2 py-0.5 rounded-md text-xs font-mono uppercase tracking-wider ${STATUS_TONE[status] || STATUS_TONE.Open}`}>
      {status}
    </span>
  );
}

export function PriorityPill({ priority }) {
  return (
    <span className={`inline-flex items-center border px-2 py-0.5 rounded-md text-[11px] font-mono uppercase ${PRIORITY_TONE[priority] || PRIORITY_TONE.medium}`}>
      {priority}
    </span>
  );
}

export default function Cases() {
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const createCase = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await api.post("/cases", form);
      toast.success("Case created");
      setForm({ title: "", description: "", priority: "medium" });
      setShowForm(false);
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create case");
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="cases-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
            <FolderKanban className="h-4 w-4" /> Case management
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Cases</h1>
          <p className="text-sm text-slate-500 mt-1">Group advertisements, evidence and notes into shared investigations.</p>
        </div>
        {!readOnly && (
          <Button data-testid="new-case-btn" onClick={() => setShowForm((s) => !s)} className="bg-slate-900 hover:bg-slate-800 gap-1.5">
            <Plus className="h-4 w-4" /> New case
          </Button>
        )}
      </header>

      {showForm && !readOnly && (
        <form onSubmit={createCase} data-testid="case-form" className="border border-slate-200 bg-white rounded-md p-5 space-y-3">
          <Input data-testid="case-title-input" placeholder="Case title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <Textarea data-testid="case-desc-input" placeholder="Short description / hypothesis" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex items-center gap-3">
            <select data-testid="case-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="border border-slate-300 rounded-md text-sm px-2 py-2">
              <option value="low">Low priority</option>
              <option value="medium">Medium priority</option>
              <option value="high">High priority</option>
            </select>
            <Button data-testid="case-create-submit" type="submit" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="cases-loading">Loading cases…</div>
      ) : cases.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500" data-testid="cases-empty">
          No cases yet. {readOnly ? "You will see cases you are added to here." : "Create your first case to start grouping evidence."}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cases.map((c) => (
            <Link key={c.id} to={`/cases/${c.id}`} data-testid={`case-card-${c.id}`}
              className="border border-slate-200 bg-white rounded-md p-4 hover:border-slate-400 transition-colors space-y-3 block">
              <div className="flex items-start justify-between gap-2">
                <StatusPill status={c.status} />
                <PriorityPill priority={c.priority} />
              </div>
              <div className="font-display font-bold tracking-tight text-base leading-snug line-clamp-2">{c.title}</div>
              <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
                <span>{c.ad_count} ad{c.ad_count === 1 ? "" : "s"}</span>
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{1 + (c.collaborators?.length || 0)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
